import { GanttTask } from '../types/gantt';
import { parseFlexibleDate } from './dateUtils';

/**
 * Parses raw text from Excel (TSV), Markdown tables, or CSV into GanttTask items.
 */
export function parseTableText(rawText: string): { tasks: GanttTask[]; error?: string } {
  if (!rawText || !rawText.trim()) {
    return { tasks: [], error: 'Input text is empty.' };
  }

  const lines = rawText
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    return { tasks: [], error: 'No valid rows found.' };
  }

  const parsedRows: Array<{ activity: string; start: string; end: string; progress?: number }> = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Skip markdown separator rows like |---|---|---|
    if (/^\|?(\s*:?-+:?\s*\|?)+$/.test(line)) {
      continue;
    }

    let cells: string[] = [];

    if (line.includes('\t')) {
      // Excel / Google Sheets TSV
      cells = line.split('\t').map((c) => c.trim());
    } else if (line.includes('|')) {
      // Markdown table
      cells = line
        .split('|')
        .map((c) => c.trim())
        .filter((c, idx, arr) => {
          // Remove leading and trailing empty entries if line starts/ends with |
          if ((idx === 0 || idx === arr.length - 1) && c === '') return false;
          return true;
        });
    } else if (line.includes(',')) {
      // CSV format
      cells = parseCsvLine(line);
    } else if (line.includes(';')) {
      // Semicolon separated
      cells = line.split(';').map((c) => c.trim());
    } else {
      // Whitespace separated (at least 2 consecutive spaces or tabs)
      cells = line.split(/\s{2,}/).map((c) => c.trim());
    }

    // Filter out completely blank cells
    cells = cells.filter((c) => c !== undefined && c !== null);

    if (cells.length >= 3) {
      const activity = cells[0];
      const startCandidate = parseFlexibleDate(cells[1]);
      const endCandidate = parseFlexibleDate(cells[2]);

      // If this line is the header line (e.g. Activity | Start | End)
      if (
        i === 0 &&
        !startCandidate &&
        !endCandidate &&
        (/activity|task|name/i.test(activity) || /start|begin/i.test(cells[1]))
      ) {
        // Skip header
        continue;
      }

      let progressVal: number | undefined = undefined;
      if (cells.length >= 4) {
        const pMatch = cells[3].match(/(\d+)/);
        if (pMatch) {
          progressVal = Math.min(100, Math.max(0, parseInt(pMatch[1], 10)));
        }
      }

      if (activity && startCandidate) {
        parsedRows.push({
          activity,
          start: startCandidate,
          end: endCandidate || startCandidate,
          progress: progressVal,
        });
      }
    } else if (cells.length === 2) {
      // Milestone row or single date row
      const activity = cells[0];
      const dateCandidate = parseFlexibleDate(cells[1]);
      if (activity && dateCandidate) {
        parsedRows.push({
          activity,
          start: dateCandidate,
          end: dateCandidate,
          progress: 0,
        });
      }
    }
  }

  if (parsedRows.length === 0) {
    return {
      tasks: [],
      error: 'Could not detect date columns. Expected format: Activity | Start Date | End Date',
    };
  }

  const tasks: GanttTask[] = parsedRows.map((row, index) => ({
    id: `task-${Date.now()}-${index}`,
    activity: row.activity,
    start: row.start,
    end: row.end,
    progress: row.progress !== undefined ? row.progress : 100,
  }));

  return { tasks };
}

function parseCsvLine(text: string): string[] {
  const result: string[] = [];
  let cur = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '"' || char === "'") {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(cur.trim());
      cur = '';
    } else {
      cur += char;
    }
  }
  result.push(cur.trim());
  return result;
}
